import React, { createContext, useContext, useEffect } from 'react'
import {
  getOutages as getAfgOutages,
  OutageState,
  mortgage,
  auto,
  creditCard,
  deposit,
  invest,
} from '@ally/data-selectors-afg-accounts'
import { LoadState } from '@ally/use-data-domain'
import { Account, AccountType } from '@ally/data-types-afg-accounts'
import { uniq } from 'ramda'
import { useHostServices } from './HostServices'
import {
  calculateTotalBalance,
  filterActiveDepositAccounts,
  filterOpenDepositAccounts,
  getRoles,
  getStkAccounts,
  getStkSummary,
  groupBankAccounts,
  groupInvestAccounts,
  hasStaleAccounts,
} from './utils'
import { GroupedAccounts, StkSummary } from './types'

type AccountTypeContextProps = {
  activeDepositAccounts: Account[]
  activeNonRetirementDepositAccounts: Account[]
  allCCAccounts: Account[]
  groupedAccounts: GroupedAccounts
  groupedBankAccounts: GroupedAccounts
  groupedInvestAccounts: GroupedAccounts
  hasActiveDepositAccounts: boolean
  hasAtLeastOneActiveAccount: boolean
  hasCCAccounts: boolean
  hasInvestAccounts: boolean
  hasOpenSdaAccounts: boolean
  hasSingleAccountType: boolean
  hasStaleBankAccounts: boolean
  hasStaleCCAccounts: boolean
  hasStaleInvestAccounts: boolean
  investAccounts: Account[]
  isAccountDataLoaded: boolean
  isAutoOnlyOutage: boolean
  isAutoOnlyUser: boolean
  isAutoOutage: boolean
  isBankOnlyOutage: boolean
  isBankOnlyUser: boolean
  isBankOutage: boolean
  isBankUser: boolean
  isCCFullOutage: boolean
  isCCUser: boolean
  isCCOnlyUser: boolean
  isFullOutage: boolean
  isInvestOnlyOutage: boolean
  isInvestOnlyUser: boolean
  isInvestOutage: boolean
  isInvestUser: boolean
  isMortgageFullOutage: boolean
  isMortgageOnlyUser: boolean
  isMortgageUser: boolean
  isPartialOutage: boolean
  isSingleLobUser: boolean
  isSingleLobUserOutage: boolean
  isWealthClient: boolean
  isWealthFullOutage: boolean
  isWealthPartialOutage: boolean
  isWealthProspect: boolean
  isWealthUser: boolean
  openDepositAccounts: Account[]
  stkAccounts: Account[]
  stkSummary: StkSummary
  totalBalanceOfBankAndInvest: number
  allMortgageAccounts: Account[]
  allAutoAccounts: Account[]
  isAutoUser: boolean
  isDebtPlanUser: boolean
  isDebtPlanOnlyUser: boolean
}

export const AccountTypeContext = createContext<AccountTypeContextProps>(
  (null as unknown) as AccountTypeContextProps,
)

export const useAccountTypes = (): AccountTypeContextProps =>
  useContext(AccountTypeContext)

export const AccountTypeProvider: React.FC = ({ children }) => {
  const { featureFlags, hostData, session, log } = useHostServices()
  const isAutoEnabled = featureFlags.variation('FF_SPOG_WEB_UI', false)
  const isCCEnabled = featureFlags.variation('FF_credit-card-snapshot', false)
  const isDebtPlanEnabled = featureFlags.variation(
    'FF_debt-plan-snapshot',
    false,
  )
  const investId = session.data?.investId

  const isAccountDataLoaded =
    hostData.afgAccounts.status.loadState === LoadState.LOADED
  // all deposit accounts including: retirement and closed
  const depositAccounts = deposit.getAll(hostData)

  // filterActiveDepositAccounts is still showing accounts closed within 60 days
  const activeDepositAccounts = filterActiveDepositAccounts(depositAccounts)
  const activeNonRetirementDepositAccounts = activeDepositAccounts.filter(
    acc => !!acc.indicator?.retirement === false,
  )
  // filter closed accounts regardless of date
  const openDepositAccounts = filterOpenDepositAccounts(depositAccounts)

  const investAccounts = invest.getAll(hostData)

  const hasInvestAccounts = investAccounts && investAccounts.length > 0

  const totalBalanceOfBankAndInvest = calculateTotalBalance(
    depositAccounts,
    investAccounts,
  )
  const roles = getRoles(session)

  // LOB Users
  const isMortgageUser = roles.mortgage
  const isBankUser = roles.bank
  const isWealthUser = roles.wealth
  const isInvestUser =
    roles.investment ||
    (!!investId && !isWealthUser) || // HACK: (invest backfill issue) will give a false positive for a deposit only customer with an investmentId (scenario: exited wealth prospect assessment without completing it), accepted risk
    (!!investId && hasInvestAccounts) // HACK: (invest backfill issue) investId alone is not sufficient to determine an invest customer as wealth customers have an investmentId

  const isDebtPlanUser = isDebtPlanEnabled && roles.debtPlan
  const isCCUser = isCCEnabled && roles.creditcard
  const isBankOnlyUser = roles.isOnly('bank')
  const isInvestOnlyUser = roles.isOnly('investment')
  const isMortgageOnlyUser = roles.isOnly('mortgage')
  const isCCOnlyUser = isCCEnabled && roles.isOnly('creditcard')
  const isDebtPlanOnlyUser = roles.isOnly('debtPlan')

  // TODO [DEBT_PLAN] debtPlan relationship?
  const isSingleLobUser = !!(
    isBankOnlyUser ||
    isInvestOnlyUser ||
    isMortgageOnlyUser ||
    isCCOnlyUser
  )
  const isAutoUser = !!(isAutoEnabled && roles.auto)
  const isAutoOnlyUser = roles.isOnly('auto')
  const wealthStatus = session.data?.relationships?.invest?.wealth?.status
  const isWealthProspect = !!(isWealthUser && wealthStatus === 'PROSPECT')
  const isWealthClient = !!(
    isWealthUser &&
    (wealthStatus === 'CLIENT_FUNDED' || wealthStatus === 'CLIENT_UNFUNDED')
  )

  // use activeDepositAccounts and investAccounts to pass into account cards in case of partial outage.
  const hasActiveDepositAccounts = activeDepositAccounts.length > 0

  const hasOpenSdaAccounts =
    openDepositAccounts.filter(account => account.type === AccountType.SDA)
      .length > 0

  const groupedBankAccounts = groupBankAccounts(activeDepositAccounts)
  const groupedInvestAccounts = groupInvestAccounts(investAccounts)
  const groupedAccounts = { ...groupedBankAccounts, ...groupedInvestAccounts }
  const stkSummary = getStkSummary(activeDepositAccounts)
  const stkAccounts = getStkAccounts(activeDepositAccounts)

  const {
    AUTO: autoOutageState,
    DEPOSIT: depositOutageState,
    INVEST: investOutageState,
    WEALTH: wealthOutageState,
    MORTGAGE: mortgageOutageState,
    CC: creditCardOutageState,
  } = getAfgOutages(hostData)

  const isFull = (outageState: OutageState): boolean => outageState === 'full'
  const isPartial = (outageState: OutageState): boolean =>
    outageState === 'partial'

  const isAutoOutage = isAutoUser && isFull(autoOutageState)
  const isAutoOnlyOutage = isAutoOnlyUser && isFull(autoOutageState)
  const isBankOutage = isBankUser && isFull(depositOutageState)
  const isBankOnlyOutage = isBankOnlyUser && isFull(depositOutageState)
  const isInvestOutage = isFull(investOutageState)
  const isInvestOnlyOutage = isInvestOnlyUser && isFull(investOutageState)
  const isWealthFullOutage = isWealthUser && isFull(wealthOutageState)
  const isWealthPartialOutage = isWealthUser && isPartial(wealthOutageState) // unable to fetch data from Orion service (ex. yield and return)
  const isMortgageFullOutage = isMortgageUser && isFull(mortgageOutageState)
  const isMortgageOnlyOutage = isMortgageOnlyUser && isFull(mortgageOutageState)
  const isCCFullOutage = isCCUser && isFull(creditCardOutageState)
  const isSingleLobUserOutage =
    isBankOnlyOutage || isInvestOnlyOutage || isMortgageOnlyOutage // Wealth cannot be the only Lob

  const roleOutages = [
    [isAutoUser, isAutoOutage],
    [isBankUser, isBankOutage],
    [isInvestUser, isInvestOutage],
    [isWealthUser, isWealthFullOutage],
    [isMortgageUser, isMortgageFullOutage],
    [isCCUser, isCCFullOutage],
  ]

  const isFullOutage = roleOutages
    .filter(([hasRole]) => hasRole)
    .every(([_, hasOutage]) => hasOutage)

  const isPartialOutage =
    !isFullOutage &&
    (isBankOutage ||
      isInvestOutage ||
      isWealthFullOutage ||
      isMortgageFullOutage)

  const allMortgageAccounts = mortgage.getAll(hostData)
  const hasMortgageAccounts = allMortgageAccounts.length > 0

  const allCCAccounts = isCCEnabled ? creditCard.getValid(hostData) : []
  const hasCCAccounts = allCCAccounts.length > 0

  // used for showing the customization trigger next to the LOB name
  const bankInvestCreditCardTypes = [
    ...Object.keys(groupedBankAccounts),
    ...Object.keys(groupedInvestAccounts),
    ...uniq(allCCAccounts.map(acc => acc.type)),
  ]
    // we need to add an element to this array if they are a debtPlan user since it is not considered an LOB
    .concat(isDebtPlanUser ? ['DEBT_PLAN'] : [])

  /*
    MORTGAGE does not have accountTypes separated by sections in the UI.
    If MORTGAGE is the only LOB, we need to consider that is a "single accountType"
    as it concerns to the UI even though there are MORTGAGE and HOME_EQUITY account types.
  */
  const hasSingleAccountType = hasMortgageAccounts
    ? bankInvestCreditCardTypes.length === 0
    : bankInvestCreditCardTypes.length === 1

  const hasStaleBankAccounts = hasStaleAccounts(depositAccounts)
  const hasStaleCCAccounts = hasStaleAccounts(allCCAccounts)
  const hasStaleInvestAccounts = hasStaleAccounts(investAccounts)

  useEffect(() => {
    if (hasStaleBankAccounts) {
      log.track('received-always-on-investments')
    }
    if (hasStaleCCAccounts) {
      log.track('received-always-on-creditcard')
    }
    if (hasStaleInvestAccounts) {
      log.track('received-always-on-deposits')
    }
  }, [])

  const allAutoAccounts = isAutoUser ? auto.getAll(hostData) : []
  const hasAutoAccounts = allAutoAccounts.length > 0

  // As LOBs are added we will need to update this value
  const hasAtLeastOneActiveAccount =
    hasInvestAccounts ||
    hasActiveDepositAccounts ||
    hasMortgageAccounts ||
    hasAutoAccounts ||
    isDebtPlanUser ||
    hasCCAccounts

  return (
    <AccountTypeContext.Provider
      value={{
        activeDepositAccounts,
        activeNonRetirementDepositAccounts,
        allAutoAccounts,
        allCCAccounts,
        allMortgageAccounts,
        groupedAccounts,
        groupedBankAccounts,
        groupedInvestAccounts,
        hasActiveDepositAccounts,
        hasAtLeastOneActiveAccount,
        hasCCAccounts,
        hasInvestAccounts,
        hasOpenSdaAccounts,
        hasSingleAccountType,
        hasStaleBankAccounts,
        hasStaleCCAccounts,
        hasStaleInvestAccounts,
        investAccounts,
        isAccountDataLoaded,
        isAutoOnlyOutage,
        isAutoOnlyUser,
        isAutoOutage,
        isAutoUser,
        isBankOnlyOutage,
        isBankOnlyUser,
        isBankOutage,
        isBankUser,
        isCCFullOutage,
        isCCUser,
        isCCOnlyUser,
        isDebtPlanOnlyUser,
        isDebtPlanUser,
        isFullOutage,
        isInvestOnlyOutage,
        isInvestOnlyUser,
        isInvestOutage,
        isInvestUser,
        isMortgageFullOutage,
        isMortgageOnlyUser,
        isMortgageUser,
        isPartialOutage,
        isSingleLobUser,
        isSingleLobUserOutage,
        isWealthClient,
        isWealthFullOutage,
        isWealthPartialOutage,
        isWealthProspect,
        isWealthUser,
        openDepositAccounts,
        stkAccounts,
        stkSummary,
        totalBalanceOfBankAndInvest,
      }}
    >
      {children}
    </AccountTypeContext.Provider>
  )
}
